import React, { useContext, useMemo, useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { StoreContext } from "../../store";
import { Box, Dialog, DialogContent, DialogTitle, Typography, makeStyles, IconButton } from "@material-ui/core";
import { Warning as WarningIcon, Close as CloseIcon } from "@material-ui/icons";
import { ManageRoomForm } from "./";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import { toJS } from "mobx";
import useFillStore from "../../hooks/useFillStore";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
  },
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: "#fafafa",
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  warningIcon: {
    color: theme.palette.warning.main,
    fontSize: "2rem",
  },
  alertBox: {
    backgroundColor: "rgba(255, 152, 0, 0.05)",
    border: `1px solid rgba(255, 152, 0, 0.2)`,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
}));

const excludedPaths = [
  "/signin",
  "/signup",
  "/forgotpassword",
  "/resetpassword",
  "/onboarding",
  "/firstaccess",
  "/404",
  "/trial-expired",
  "/verification-failed",
  "/subscription",
  "/subscription/manage-room",
  "/subscription/manage-plan",
  "/subscription/manage-delayed-disable",
];

/**
 * Fetch Data
 */
async function fetchData(store, router, excludedPaths) {
  const isExcludedPage = excludedPaths.includes(router.pathname);
  const validCredential = store.user?.signedIn && store.organization?.selected?._id;
  const allowChecking = validCredential && store.subscription.selected && !isExcludedPage;
  if (!allowChecking) {
    return;
  }

  const results = await Promise.all([
    store.subscription.fetchByRealmId(store.organization.selected._id),
    store.property.fetch(),
    store.room.fetch(),
  ]);

  return { allowChecking, results };
}

/**
 * RoomCapacityGuard Component
 *
 * A persistent guard that monitors if the user's enabled rooms exceed their purchased limit.
 * If over capacity, it forces a modal to disable excess rooms.
 */
const RoomCapacityGuard = observer(() => {
  const { t } = useTranslation("common");
  const classes = useStyles();
  const store = useContext(StoreContext);
  const router = useRouter();
  const [isSyncing, setIsSyncing] = useState(false);

  // 1. Fetch necessary data if not already present
  const [fetching] = useFillStore(fetchData, [router, excludedPaths]);

  // 2. Determine if we should even evaluate the guard
  const isExcludedPage = useMemo(() => {
    return excludedPaths.includes(router.pathname);
  }, [excludedPaths, router.pathname]);

  const canCheck = store.user.signedIn && !isExcludedPage && store.subscription.selected;

  // 3. Calculate capacity status
  const status = useMemo(() => {
    // Only check if we have the data. If room items is undefined, we haven't loaded yet.
    if (!canCheck || store.room?.items === undefined) return { isOverCapacity: false };

    const limit = store.subscription.selected?.usage?.activeRoom ?? 0;
    const enabledCount = store.subscription.selected?.enabledRoom ?? 0;

    return {
      isOverCapacity: enabledCount > limit,
      limit,
      enabledCount,
      excess: enabledCount - limit,
    };
  }, [canCheck, store.subscription.selected, store.room?.items]);

  // 4. Prepare data for ManageRoomForm
  const formData = useMemo(() => {
    if (!status.isOverCapacity || !canCheck) return null;

    const properties = Array.isArray(store.property?.items)
      ? store.property.items.map((p) => ({
          id: p._id,
          name: p.name,
        }))
      : [];

    const rooms = Array.isArray(store.room?.items)
      ? store.room.items.map((r) => ({
          id: r._id,
          propertyId: r.property?.id || r.propertyId,
          name: r.name,
          enabled: r.isEnabled,
          vacant: r.room_occupancy === "vacant",
        }))
      : [];

    return { properties, rooms };
  }, [status.isOverCapacity, store.property?.items, store.room?.items]);

  const handleRoomSubmit = useCallback(
    async (formRooms) => {
      setIsSyncing(true);
      try {
        const roomPayload = formRooms.map((r) => ({
          id: r.id,
          enabled: r.enabled,
        }));

        const response = await store.subscription.updateRoomStatuses(store.subscription.selected.id, roomPayload);
        if (response.status === 200) {
          store.pushToastMessage({
            message: t("Room status synced successfully."),
            severity: "success",
          });
          // After sync, the store should update and the modal will naturally close
          // but we might need to re-fetch rooms to be sure
          await store.room.fetch();
        } else {
          store.pushToastMessage({
            message: t(`${response?.data?.error}`) || t("Failed to update room status."),
            severity: "error",
          });
        }
      } finally {
        setIsSyncing(false);
      }
    },
    [store.subscription, store.room, t],
  );

  if (store.subscription.isStarterPlan || !canCheck || !status.isOverCapacity || !formData) return null;

  return (
    <Dialog open={true} fullWidth maxWidth="lg" disableBackdropClick disableEscapeKeyDown scroll="body">
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.headerBox}>
          <WarningIcon className={classes.warningIcon} />
          <Box>
            <Typography variant="h6" style={{ fontWeight: 800 }}>
              {t("Action Required")}: {t("Room Capacity Exceeded")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("Your current plan limit has been reached.")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.alertBox}>
          <Typography variant="body2" style={{ fontWeight: 600, color: "#856404" }}>
            {t("You currently have {{count}} rooms enabled, but your purchased limit is {{limit}}.", {
              count: status.enabledCount,
              limit: status.limit,
            })}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 4, color: "#856404" }}>
            {t("Please disable at least {{excess}} rooms to continue using the application.", {
              excess: status.excess,
            })}
          </Typography>
        </Box>

        <ManageRoomForm
          activeRoomLimit={status.limit}
          properties={formData.properties}
          rooms={formData.rooms}
          onSubmit={handleRoomSubmit}
        />

        {isSyncing && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgcolor="rgba(255,255,255,0.7)"
            zIndex={2000}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="h6" color="primary" style={{ fontWeight: 700 }}>
              {t("Syncing changes...")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default RoomCapacityGuard;
